'use client';

import { styled } from 'styled-components';
import type { QuoteCardProps } from '@dvd/components/QuoteCard';
import QuoteCard from '@dvd/components/QuoteCard';
import type { CardColor } from '@types';
import ContentfulImage from '@dvd/components/ContentfulImage';

type CustomerStoryProps = QuoteCardProps & {
  /**
   * Color scheme of the card; do not set if card is centered
   */
  colorScheme?: CardColor | 'transparent';
  /**
   * The author of the quote on the card
   */
  attribution: string;
  /**
   * The title of the attributing author
   */
  attributionTitle: string;
  /**
   * The card quote
   */
  quote: string;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * optional image that will be beside the quote for the card
   * @default null
   */
  featureImageProps?: React.ComponentProps<typeof ContentfulImage> | null;
  /**
   * Align start or center; default is center
   */
  alignment?: 'center' | 'start';
};

const CustomerStoryLayout = styled.div<{
  $alignment: string;
  $hasFeatureImage: boolean;
}>`
  display: grid;
  gap: ${({ theme }) => `${theme.spacing['2']}`};
  grid-template-columns: 1fr;
  margin-block: ${({ theme }) => `${theme.spacing[3]}`};

  .customerStoryQuote .customerStoryImage {
    padding-block-end: ${({ theme }) => theme.spacing['1']};
  }

  .quoteCard {
    gap: ${({ theme }) => theme.spacing['2']};
    place-items: ${({ $alignment }) => $alignment};
    text-align: ${({ $alignment }) => $alignment};

    p {
      /** Remove top padding from quote text */
      padding-top: 0;
    }
  }

  ${({ theme, $hasFeatureImage }) =>
    $hasFeatureImage ? theme.mq.minWidth.medium : theme.mq.minWidth.large} {
    margin-block: ${({ theme }) => theme.spacing[4]};

    ${({ theme, $hasFeatureImage }) =>
      !$hasFeatureImage && theme.grid.columns.large};
    .customerStoryQuote {
      grid-column: 3 / span 8;
    }

    ${({ $hasFeatureImage, theme }) =>
      $hasFeatureImage &&
      `
      align-items: center;
      grid-template-columns: minmax(200px, 1.25fr) 1fr;

      .customerStoryQuote {
          grid-column: 1;
          height: 100%;
      }

      .customerStoryImage { 
        grid-column: 2; 
      };

      .quoteCard {
        padding: ${theme.spacing[2]};
      }
    `}
  }
`;

const CustomerStory = ({
  alignment = 'center',
  className,
  colorScheme,
  featureImageProps,
  attribution,
  quote,
  attributionTitle,
  ...logoProps
}: CustomerStoryProps) => {
  const hasFeatureImage = Boolean(featureImageProps);

  return (
    <CustomerStoryLayout
      $alignment={alignment}
      $hasFeatureImage={hasFeatureImage}
    >
      <div className="customerStoryQuote">
        <QuoteCard
          attribution={attribution}
          attributionTitle={attributionTitle}
          className={className}
          colorScheme={alignment === 'center' ? 'transparent' : colorScheme}
          {...logoProps}
          quote={quote}
        />
      </div>
      {hasFeatureImage ? (
        <ContentfulImage
          className="customerStoryImage"
          {...featureImageProps}
        />
      ) : null}
    </CustomerStoryLayout>
  );
};

export default CustomerStory;
